import { CSSResultGroup, TemplateResult } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property } from 'lit/decorators.js';
// import { classMap } from 'lit/directives/class-map.js';
import componentStyles from './tufts-list.css.lit';
import { SlotNGController } from '../../controllers/slot-ng-controller';
import { html } from 'lit/static-html.js';

export const listTypes = ['ol', 'ul', 'div'] as const;

export type ListType = (typeof listTypes)[number];

export const listOrientations = ['column', 'row', 'col-center'] as const;

export type ListOrientation = (typeof listOrientations)[number];

export type ClassInfo = { [name: string]: string | boolean | number };

/**
 * The TuftsList component
 * @element tufts-list
 * @extends OutlineElement
 *
 * @slot default slot.
 *
 * @attr list-type
 * @attr orientation
 * @attr nav-label
 * @attr gap
 */
@customElement('tufts-list')
export class TuftsList extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  ngSlot = new SlotNGController(this);

  /**
   * Determines which type of list is rendered.
   * ol | ul | div
   */
  @property({ type: String, attribute: 'list-type' })
  listType: ListType = 'ul';

  /**
   * Sets orientation of list.
   * column | row
   */
  @property({ type: String })
  orientation: ListOrientation = 'column';

  /**
   * If set will wrap list in a nav tag
   * with the passed string set as the aria-label.
   */
  @property({ type: String, attribute: 'nav-label' })
  navLabel: string | undefined;

  /**
   * If set will wrap list in a nav tag
   * with the passed string set as the aria-label.
   */
  @property({ type: String, attribute: 'gap' })
  gap = 'space-xs-s';

  wrap(el: Element, wrapperElement = 'li') {
    if (el && el.parentNode) {
      // create wrapper container
      const wrapper = document.createElement(wrapperElement);

      // insert wrapper before el in the DOM tree
      el.parentNode?.insertBefore(wrapper, el);
      wrapper.appendChild(el);
    }
  }

  /**
   * Wrap an array of items all at once
   */
  wrapAll(ary: HTMLCollection, wrapperElement: ListType) {
    if (ary && ary.length) {
      const wrapper = document.createElement(wrapperElement);
      wrapper.classList.add('list', this.orientation);
      const originalArrayLength = ary.length;
      for (let i = 0; i < originalArrayLength; i++) {
        wrapper.appendChild(ary[0]);
      }
      this.appendChild(wrapper);
    }
  }

  wrapEachElement() {
    Array.from(this.children).forEach(item => {
      return this.wrap(item, 'li');
    });
  }

  firstUpdated(): void {
    this.wrapEachElement();
    this.wrapAll(this.children, this.listType);
    this.requestUpdate();
  }

  render(): TemplateResult {
    return html` ${this.gap
      ? html` <style>
          .list {
            gap: var(--${this.gap});
          }
        </style>`
      : null}
    ${this.navLabel
      ? html`
          <nav aria-label="${this.navLabel}">
            ${this.ngSlot.renderInShadow()}
          </nav>
        `
      : html` ${this.ngSlot.renderInShadow()} `}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-list': TuftsList;
  }
}
